import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatTheme } from '@/chat/ui/index.js';
import { KnowledgeDrawer } from './KnowledgeDrawer.jsx';
import { useHostChat } from '../../application/context';
import { RESOLVED_UI_LABELS } from '../config/constants.js';
import { selectActiveSession } from '../../projection/session.js';
import { useSources } from '../../application/sources/provider';
import { useProjectNameEditing } from '../session/useProjectNameEditing.js';
import { usePanels } from './panels/panels.js';
import { useComposerController } from '../composer/useComposer.jsx';
import { useCanvasController } from '../canvas/useCanvas.jsx';
import { useScenarioPromptCards } from '../recommendations/useCards.js';
import { useOperations } from '../../application/operations/provider';
import { useArtifacts } from '../../application/artifacts/provider';
import { useControllerSessionWorkspaceReset } from './useSessionReset.js';
import { selectProjectSlot } from '../../application/projects/project-selection.js';

const CONTROLLER_DEFAULTS = Object.freeze({
  functionType: '', defaultSessionTitle: '', projectId: '', userId: '', backendMode: '',
  kernelBaseUrl: '', apiBaseUrl: '', apiToken: '', defaultProjectName: '', instanceId: '',
  domainPackDomain: '', domainPackVersion: '', branding: {}, uiLabels: {}, displayPolicy: {},
  starterScenarios: [], composerModeOptions: [], capabilityCatalog: [], modulePromptRegistry: [],
});

const asArray = (value) => (Array.isArray(value) ? value : []);
const resolveUILabels = (uiLabels) => ({
  ...RESOLVED_UI_LABELS,
  ...(uiLabels && typeof uiLabels === 'object' && !Array.isArray(uiLabels) ? uiLabels : {}),
});
const resolveProjectDisplayName = (projectSlot, projectDisplayName) => String(
  Object(projectSlot).name || projectDisplayName || '',
).trim();
export function useController(options = {}) {
  const {
    userId, defaultProjectName, uiLabels, displayPolicy,
    starterScenarios, composerModeOptions, capabilityCatalog, modulePromptRegistry,
  } = { ...CONTROLLER_DEFAULTS, ...options };
  const { workspaceLocation, apiReadinessGate, blockedApiError, viewModel, modeManagement, skillManagement } = useHostChat();
  const effectiveProjectId = workspaceLocation.effectiveProjectId;
  const { clearSelection } = useArtifacts();
  const { themeTokens } = useChatTheme();
  const resolvedUILabels = useMemo(() => resolveUILabels(uiLabels), [uiLabels]);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('chats');
  const [canvasFullscreenOpen, setCanvasFullscreenOpen] = useState(false);
  const handleCanvasPanelClosed = useCallback(() => setCanvasFullscreenOpen(false), []);
  const {
    knowledgeHubPopoverOpen,
    setKnowledgeHubPopoverOpen,
    showCanvasPanel,
    setShowCanvasPanel,
    canvasActiveTabKey,
    setCanvasActiveTabKey,
    canvasTargetFocusKey,
    handleOpenKnowledgeHub,
    handleToggleWorkspacePanel,
    handleCloseWorkspacePanel,
  } = usePanels({ onCanvasPanelClosed: handleCanvasPanelClosed });
  useEffect(() => {
    if (!showCanvasPanel) clearSelection();
  }, [clearSelection, showCanvasPanel]);
  const sessions = asArray(viewModel.sessionList.sessions);
  const activeSessionId = viewModel.sessionList.activeSessionId;
  const activeSession = selectActiveSession(sessions, activeSessionId);
  const projectItems = asArray(viewModel.sessionList.projectItems);
  const projectSlot = selectProjectSlot(projectItems, effectiveProjectId);
  const {
    projectNameEditing,
    projectNameDraft,
    setProjectNameDraft,
    projectDisplayName,
    handleProjectNameStartEdit,
    handleProjectNameCancel,
    handleProjectNameSave,
  } = useProjectNameEditing({
    defaultProjectName,
    projectSlot,
    onRenameProject: viewModel.sessionList.onRenameProject,
  });
  const {
    sourceActionError, sourceItems, sourceRemovingId, sourceSyncLoading, sourceUploadLoading,
    sourceUploadProgressItems, uploadSourceFiles, handleRemoveSource, handleRetrySource,
    handleViewSourceDetail, getDomainFields, setShouldLoadSources,
  } = useSources();
  const sourceFileInputRef = useRef(null);
  useEffect(() => {
    setShouldLoadSources(Boolean(effectiveProjectId) && (knowledgeHubPopoverOpen || activeWorkspaceTab === 'sources'));
    return () => setShouldLoadSources(false);
  }, [activeWorkspaceTab, effectiveProjectId, knowledgeHubPopoverOpen, setShouldLoadSources]);
  const handleOpenSourcePicker = useCallback((files = []) => {
    if (sourceUploadLoading) return;
    if (Array.isArray(files) && files.length) { void uploadSourceFiles(files); return; }
    sourceFileInputRef.current?.click();
  }, [sourceUploadLoading, uploadSourceFiles]);
  const handleSourceFileChange = useCallback((event) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void uploadSourceFiles(files);
  }, [uploadSourceFiles]);
  const domainFieldAPI = useMemo(() => ({ getDomainFields }), [getDomainFields]);
  const hasProject = Boolean(projectSlot);
  const resolvedProjectDisplayName = resolveProjectDisplayName(projectSlot, projectDisplayName);
  useControllerSessionWorkspaceReset({
    activeSessionId,
    sidebarNavigation: viewModel.sessionList.sidebarNavigation?.navigation,
    setActiveWorkspaceTab,
    setCanvasActiveTabKey,
    setCanvasFullscreenOpen,
    setKnowledgeHubPopoverOpen,
    setShowCanvasPanel,
  });


  const handleProjectNameStartEditForSlot = () => {
    handleProjectNameStartEdit(projectSlot);
  };

  const {
    actionGuards,
    inputState,
    composerNode,
    composerModeKey,
    evidenceStrictMode,
    evidenceHitScoreThreshold,
    handleSendWithSourceRefs,
  } = useComposerController({
    userId,
    projectId: effectiveProjectId,
    activeSessionId,
    viewModel,
    apiReadinessGate,
    blockedApiError,
    sourceItems, sourceUploadLoading, sourceUploadProgressItems,
    activeWorkspaceTab, showCanvasPanel, canvasActiveTabKey,
    canvasTargetFocusKey,
    setActiveWorkspaceTab,
    setShowCanvasPanel,
    setCanvasActiveTabKey,
    themeTokens,
    composerModeOptions: modeManagement?.projection?.composer_options ?? composerModeOptions,
    capabilityCatalog, modulePromptRegistry,
    displayPolicy,
    resolvedUILabels,
  });


  const {
    canExpandScenarios,
    recommendationPanelDismissed,
    recommendationPanelTitle,
    showAllScenarios,
    setShowAllScenarios,
    visibleScenarios,
    handleScenarioCardClick,
  } = useScenarioPromptCards({
    activeSessionId,
    workspaceSurface: viewModel.sessionList.workspaceSurface,
    workspaceSuspension: viewModel.workspaceSuspension,
    resolvedUILabels,
    availability: viewModel.messageCapabilities,
    modulePromptRegistry,
    starterScenarios,
    timelineItems: viewModel.timeline.items,
    defaultShowAllScenarios: Object(displayPolicy).showAllStarterScenarios === true,
    showStarterScenarios: Object(displayPolicy).showStarterScenarios !== false,
    composerLoading: viewModel.composer.loading,
    composerError: viewModel.composer.error,
    composerOnChange: viewModel.composer.onChange,
    onMessageCapabilityAction: viewModel.composer.onMessageCapabilityAction,
  });

  const {
    canvasAvailable,
    canvasDevDiagnostics,
    canvasPanelNode,
    renderedTimelineItems,
    handleUICardAction,
    generativeRegistry,
  } = useCanvasController({
    projectId: effectiveProjectId,
    userId,
    activeSessionId,
    sessionContext: Object(viewModel.session).context || null,
    timeline: viewModel.timeline,
    composerModeKey,
    evidenceStrictMode,
    evidenceHitScoreThreshold,
    showCanvasPanel,
    setShowCanvasPanel,
    canvasActiveTabKey,
    setCanvasActiveTabKey,
    canvasFullscreenOpen,
    setCanvasFullscreenOpen,
    inputState,
    themeTokens,
    onPatchQuestionAnswerSubmit: handleSendWithSourceRefs,
  });
  const { workflow } = useOperations();
  const handleWorkflowOperationAction = workflow.handleWorkflowOperationAction;
  // Canvas rendering remains local; submission state comes from the application owner.
  const workflowActionsById = new Map(workflow.renderedTimelineItems
    .filter((item) => item.workflowOperationActions)
    .map((item) => [item.id, item.workflowOperationActions]));
  const renderedTimelineItemsWithWorkflowActions = renderedTimelineItems.map((item) => item.type === 'thinking'
      ? { ...item, workflowOperationActions: workflowActionsById.get(item.id) ?? [] } : item);

  useEffect(() => {
    if (canvasAvailable || !showCanvasPanel) {
      return;
    }
    setShowCanvasPanel(false);
    setCanvasActiveTabKey('');
  }, [canvasAvailable, setCanvasActiveTabKey, setShowCanvasPanel, showCanvasPanel]);

  const knowledgeHubPopoverContent = <KnowledgeDrawer
    resolvedUILabels={resolvedUILabels} handleOpenSourcePicker={handleOpenSourcePicker}
  />;

  return {
    modeManagement,
    skillManagement,
    themeTokens,
    viewModel,
    actionGuards,
    apiReadinessGate,
    hasProject,
    projectItems,
    projectSlot,
    activeSession,
    sessions,
    projectNameEditing,
    projectNameDraft,
    setProjectNameDraft,
    handleProjectNameSave,
    handleProjectNameCancel,
    projectDisplayName: resolvedProjectDisplayName,
    handleProjectNameStartEdit: handleProjectNameStartEditForSlot,
    activeWorkspaceTab,
    setActiveWorkspaceTab,
    knowledgeHubPopoverContent,
    knowledgeHubPopoverOpen,
    setKnowledgeHubPopoverOpen,
    handleOpenKnowledgeHub,
    showCanvasPanel,
    canvasFullscreenOpen,
    handleToggleWorkspacePanel,
    handleCloseWorkspacePanel,
    composerNode,
    handleOpenSourcePicker,
    handleRemoveSource,
    handleRetrySource,
    handleViewSourceDetail,
    domainFieldAPI,
    getDomainFields,
    sourceActionError,
    sourceItems,
    sourceRemovingId,
    sourceSyncLoading,
    sourceUploadLoading,
    canvasAvailable,
    canvasDevDiagnostics,
    canvasPanelNode,
    renderedTimelineItems: renderedTimelineItemsWithWorkflowActions,
    handleUICardAction,
    handleWorkflowOperationAction,
    generativeRegistry,
    canExpandScenarios,
    showAllScenarios,
    setShowAllScenarios,
    visibleScenarios,
    recommendationPanelDismissed,
    recommendationPanelTitle,
    handleScenarioCardClick,
    sourceFileInputRef,
    handleSourceFileChange,
  };
}

export default useController;
